import React, { useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import auditAPI from '../services/api';
import { Environment } from '../services/api';
import { getDateRangePreset } from '../utils/dateHelpers';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface Charts2DashboardProps {
  dateRange?: { start: string; end: string };
  environment?: Environment;
}

const Charts2Dashboard: React.FC<Charts2DashboardProps> = ({ dateRange, environment = 'prod' }) => {
  const [ordersSentData, setOrdersSentData] = useState<any>(null);
  const [repairsDisabledVsSentData, setRepairsDisabledVsSentData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadChartData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, environment]);

  const loadChartData = async () => {
    setLoading(true);
    try {
      const range = dateRange || getDateRangePreset('last_7d');

      // Fire all Athena queries in parallel — allSettled so one failure doesn't blank all charts
      const settled = await Promise.allSettled([
        auditAPI.getOrdersSentByCompanyDistinct(range.start, range.end, environment),
        auditAPI.getOrdersSentByCompanyAll(range.start, range.end, environment),
        auditAPI.getRepairsDisabledVsSentByCompany(range.start, range.end, environment),
      ]);
      const unwrap = (r: PromiseSettledResult<any>, fallback: any = []) => {
        if (r.status === 'fulfilled') return r.value;
        console.error('Chart query failed:', (r as PromiseRejectedResult).reason);
        return fallback;
      };
      const [distinctOrders, allOrders, disabledVsSent] = settled.map(r => unwrap(r));

      // Chart 1.0 + 1.2: merge distinct-order-count and all-sends-count by companyId
      const companyKey = (d: any) => d.companyName || d.companyId;
      const companies = Array.from(new Set([
        ...distinctOrders.map(companyKey),
        ...allOrders.map(companyKey),
      ]));
      const distinctByCompany = new Map(distinctOrders.map((d: any) => [companyKey(d), d.distinctOrderCount]));
      const allByCompany = new Map(allOrders.map((d: any) => [companyKey(d), d.totalSentCount]));

      setOrdersSentData({
        labels: companies,
        datasets: [
          {
            label: 'Distinct Orders Sent',
            data: companies.map((c) => distinctByCompany.get(c) || 0),
            backgroundColor: 'rgba(54, 162, 235, 0.7)',
            borderColor: 'rgba(54, 162, 235, 1)',
            borderWidth: 1,
          },
          {
            label: 'All Send Events',
            data: companies.map((c) => allByCompany.get(c) || 0),
            backgroundColor: 'rgba(153, 102, 255, 0.7)',
            borderColor: 'rgba(153, 102, 255, 1)',
            borderWidth: 1,
          },
        ],
      });

      setRepairsDisabledVsSentData({
        labels: disabledVsSent.map(companyKey),
        datasets: [
          {
            label: 'Repairs Disabled',
            data: disabledVsSent.map((d: any) => d.disabledCount),
            backgroundColor: 'rgba(220, 53, 69, 0.7)',
            borderColor: 'rgba(220, 53, 69, 1)',
            borderWidth: 1,
          },
          {
            label: 'Repairs Sent',
            data: disabledVsSent.map((d: any) => d.sentCount),
            backgroundColor: 'rgba(40, 167, 69, 0.7)',
            borderColor: 'rgba(40, 167, 69, 1)',
            borderWidth: 1,
          },
        ],
      });
    } catch (error) {
      console.error('Failed to load chart data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.loading}>
        <p>Loading charts...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Recommendation Sent Analytics</h2>

      <div style={styles.chartsGrid}>
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Orders Sent by Company (Distinct vs All Send Events)</h3>
          {ordersSentData && (
            <Bar
              data={ordersSentData}
              options={{
                responsive: true,
                plugins: {
                  legend: { position: 'top' },
                  tooltip: {
                    callbacks: {
                      label: function(context: any) {
                        const label = context.dataset.label || '';
                        const value = context.parsed.y || 0;
                        return `${label}: ${value}`;
                      },
                    },
                  },
                },
                scales: {
                  x: {
                    title: { display: true, text: 'Company' },
                    ticks: { maxRotation: 45, minRotation: 45 },
                  },
                  y: {
                    beginAtZero: true,
                    title: { display: true, text: 'Order Count' },
                  },
                },
              }}
            />
          )}
        </div>

        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Repairs Disabled vs Sent by Company</h3>
          {repairsDisabledVsSentData && (
            <Bar
              data={repairsDisabledVsSentData}
              options={{
                responsive: true,
                plugins: {
                  legend: { position: 'top' },
                  tooltip: {
                    callbacks: {
                      label: function(context: any) {
                        const label = context.dataset.label || '';
                        const value = context.parsed.y || 0;
                        return `${label}: ${value}`;
                      },
                    },
                  },
                },
                scales: {
                  x: {
                    title: { display: true, text: 'Company' },
                    ticks: { maxRotation: 45, minRotation: 45 },
                  },
                  y: {
                    beginAtZero: true,
                    title: { display: true, text: 'Repair Count' },
                  },
                },
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: '20px',
  },
  title: {
    marginBottom: '20px',
    color: '#333',
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    color: '#666',
  },
  chartsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
    gap: '20px',
  },
  chartCard: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  chartTitle: {
    marginTop: 0,
    marginBottom: '20px',
    fontSize: '16px',
    color: '#555',
  },
};

export default Charts2Dashboard;
